import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { parseChangelog } from "@/lib/parse-changelog";

// Render basic inline markdown: **bold**, `code`
function renderMarkdownInline(text: string) {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\*\*(.+?)\*\*|`(.+?)`/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(<strong key={key++}>{match[1]}</strong>);
    } else if (match[2]) {
      parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-muted text-xs font-mono">{match[2]}</code>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// Import CHANGELOG.md as raw text at build time
import changelogRaw from "../../../CHANGELOG.md?raw";

// Parse changelog data from CHANGELOG.md
const changelogData = parseChangelog(changelogRaw);

const categoryColors: Record<string, string> = {
  added: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
  changed: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
  fixed: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20",
  removed: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
};

export default function Changelog() {
  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Changelog</CardTitle>
          <p className="text-sm text-muted-foreground">
            All notable changes to 8020agent are documented here. We follow{" "}
            <a
              href="https://keepachangelog.com/en/1.0.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Keep a Changelog
            </a>{" "}
            and{" "}
            <a
              href="https://semver.org/spec/v2.0.0.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Semantic Versioning
            </a>
            .
          </p>
        </CardHeader>
        <CardContent className="space-y-8">
          {changelogData.map((release) => (
            <div key={release.version} className="space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold">v{release.version}</h2>
                {release.date && (
                  <span className="text-sm text-muted-foreground">
                    {release.date}
                  </span>
                )}
              </div>

              <div className="space-y-4 pl-4">
                {Object.entries(release.changes).map(([category, items]) => (
                  <div key={category} className="space-y-2">
                    <Badge
                      variant="outline"
                      className={`${categoryColors[category]} font-medium capitalize`}
                    >
                      {category}
                    </Badge>
                    <ul className="space-y-1.5 list-disc list-inside text-sm">
                      {items.map((item, index) => (
                        <li
                          key={index}
                          className="text-muted-foreground leading-relaxed"
                        >
                          {renderMarkdownInline(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
